import api from "../../config/api";
import {
  AiAnalysis,
  AiItemFeedback,
  AiMedicalReviewAdopted,
  AiMedicalReviewEnvelope,
} from "./SheAiChecklistTypes";

// Avaliação de checklist por IA de vídeo.
//
// Os endpoints do Laravel (routes/api.php): result, feedback (GET), feedback
// (POST) e reanalyze. `start`, `chat` e `fex` nunca foram portados do stub
// Python da POC — a tela chamava e tomava 404 em silêncio, o que fazia o
// painel de FEX e o playground de reanálise nunca renderizarem. Não recriar
// aqui sem a rota do lado do backend.

/** Envelope de `sheai_results` que o backend devolve por debriefing. */
export interface ChecklistResultEnvelope {
  status?: string;
  msg?: string;
  progress?: number;
  content?: { result?: AiAnalysis }[];
}

const getChecklistResult = (debriefing_id: string | number) => {
  return api.get<ChecklistResultEnvelope>(
    "/sheai/checklist/result/" + debriefing_id
  );
};

// BHCBAI-17 — revisão do professor por item. A nota oficial da estação NÃO sai
// daqui (ela vem de `debriefing/scales`); isto registra se o professor
// concordou com a IA, que é o sinal usado para melhorar o agente.
const getChecklistFeedback = (debriefing_id: string | number) => {
  return api.get<Record<string, AiItemFeedback>>(
    "/sheai/checklist/feedback/" + debriefing_id
  );
};

const postChecklistFeedback = (data: {
  debriefing_id: string | number;
  item_id: string | number;
  // Ausente = ajuste fino (só `note`): o backend mescla sem trocar a decisão.
  action?: "agree" | "partial" | "disagree" | "complement";
  adjusted_result?: string;
  note?: string;
  professor_id?: string | number | null;
}) => {
  return api.post<Record<string, AiItemFeedback>>(
    "/sheai/checklist/feedback",
    data
  );
};

// Reprocessar: republica o debriefing no pipeline de IA (tópico video-ready)
// mesmo já existindo análise — o botão "Reprocessar" da aba Dados IA. O
// resultado novo substitui o exibido quando o agente terminar; o hook
// useChecklistAiResult já reconsulta.
const postReanalyze = (debriefing_id: string | number) => {
  return api.post<{
    status: string;
    msg: string;
    message_id?: string;
    chave?: string;
    video_uri?: string;
    checklist_id?: number;
  }>("/sheai/checklist/reanalyze/" + debriefing_id);
};

// Parecer médico-acadêmico (MedGemma) sobre a análise vigente. O GET devolve o
// guardado para a rodada atual (ou null); o POST pede um novo e espera — a GPU
// é ligada sob demanda e a primeira chamada pode levar vários minutos.
const getMedicalReview = (debriefing_id: string | number) => {
  return api.get<AiMedicalReviewEnvelope>(
    "/sheai/checklist/medical-review/" + debriefing_id
  );
};

const postMedicalReview = (debriefing_id: string | number, steer?: string) => {
  return api.post<AiMedicalReviewEnvelope>(
    "/sheai/checklist/medical-review/" + debriefing_id,
    steer ? { steer } : {}
  );
};

// Grava, como Veredito do professor, o `suggested_verdict` do parecer vigente
// para um item — mesmo caminho da aba Checklist (`debriefing/scales`). O
// servidor só aceita o que o parecer sugeriu; o clique é o consentimento.
const adoptMedicalReviewVerdict = (
  debriefing_id: string | number,
  item_id: string | number
) => {
  return api.post<AiMedicalReviewAdopted>(
    "/sheai/checklist/medical-review/" + debriefing_id + "/adopt",
    { item_id }
  );
};

const SheAiChecklistService = {
  getChecklistResult,
  getChecklistFeedback,
  postChecklistFeedback,
  postReanalyze,
  getMedicalReview,
  postMedicalReview,
  adoptMedicalReviewVerdict,
};

export default SheAiChecklistService;
